/**
 * FundamentalsPanel — displays fundamental line items grouped into tabs.
 *
 * Phase 1 / G5. Shows up to 5 analytical groups (Income Statement, Valuation,
 * Profitability, Growth, Balance Sheet, Cash Flow) each as a tab with a table
 * of concept rows × period columns.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getFundamentals } from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { EmptyState, LoadingState } from "@/components/ui/States";
import type { FundamentalsGroup, PeriodSummary } from "@/lib/api/types";
import { clsx } from "clsx";

type Props = {
  securityId: number;
  /** "annual" | "quarterly" | "ttm". Default: "annual". */
  periodType?: "annual" | "quarterly" | "ttm";
  nPeriods?: number;
};

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function fmtPeriodLabel(p: PeriodSummary): string {
  if (p.period_type === "annual" && p.fiscal_year) {
    return `FY${p.fiscal_year}`;
  }
  if (p.period_type === "quarterly" && p.fiscal_year && p.fiscal_period) {
    return `Q${p.fiscal_period} ${p.fiscal_year}`;
  }
  // Fallback to period_end year
  return p.period_end.substring(0, 7);
}

function fmtValue(v: number | null | undefined, unit: string): string {
  if (v == null) return "—";
  switch (unit) {
    case "percent":
      return `${(v * 100).toFixed(1)}%`;
    case "ratio":
      return v.toFixed(2);
    case "shares":
      return fmtLargeNumber(v);
    case "currency":
    default:
      return fmtLargeNumber(v);
  }
}

function fmtLargeNumber(v: number): string {
  const abs = Math.abs(v);
  const sign = v < 0 ? "−" : "";
  if (abs >= 1e12) return `${sign}${(abs / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(2)}K`;
  return `${sign}${abs.toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function GroupTable({
  group,
  periods,
}: {
  group: FundamentalsGroup;
  periods: PeriodSummary[];
}) {
  if (group.items.length === 0) {
    return <EmptyState title="No data for this group." />;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border-subtle">
            <th className="py-2 pr-4 text-left font-medium text-foreground-secondary">
              Metric
            </th>
            {periods.map((p) => (
              <th
                key={p.period_id}
                className="px-3 py-2 text-right font-medium text-foreground-secondary"
              >
                {fmtPeriodLabel(p)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          {group.items.map((item) => (
            <tr key={item.concept} className="hover:bg-surface-hover">
              <td className="py-2 pr-4 text-foreground-primary">{item.label}</td>
              {periods.map((p) => {
                const v = item.values[p.period_id];
                return (
                  <td
                    key={p.period_id}
                    className={clsx(
                      "px-3 py-2 text-right tabular-nums",
                      v == null ? "text-foreground-muted" : "text-foreground-primary",
                    )}
                  >
                    {fmtValue(v, item.unit)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export function FundamentalsPanel({
  securityId,
  periodType = "annual",
  nPeriods = 4,
}: Props) {
  const [activeTab, setActiveTab] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["fundamentals", securityId, periodType, nPeriods],
    queryFn: () => getFundamentals(securityId, { periodType, nPeriods }),
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (query.isLoading) return <LoadingState />;
  if (query.isError) {
    // 404 = no fundamentals yet — show a friendly empty state, not an error.
    return (
      <Panel title="Fundamentals">
        <EmptyState title="No fundamental data available" description="Ingest financials first." />
      </Panel>
    );
  }

  const data = query.data!;

  if (data.groups.length === 0) {
    return (
      <Panel title="Fundamentals">
        <EmptyState title="No fundamental data available for this security." />
      </Panel>
    );
  }

  const currentTab = activeTab ?? data.groups[0]?.name ?? null;

  const action = (
    <div className="flex gap-1">
      {(["annual", "quarterly"] as const).map((pt) => (
        <a
          key={pt}
          onClick={(e) => {
            e.preventDefault();
            // Parent can re-render with new periodType if needed; for now just label.
          }}
          className={clsx(
            "rounded px-2 py-0.5 text-xs cursor-pointer",
            pt === periodType
              ? "bg-accent text-white"
              : "text-foreground-secondary hover:text-foreground-primary",
          )}
        >
          {pt === "annual" ? "Annual" : "Quarterly"}
        </a>
      ))}
    </div>
  );

  return (
    <Panel
      title={`Fundamentals — ${data.ticker}`}
      description={`${data.periods.length} periods · ${data.currency}`}
      action={action}
    >
      {/* Tab strip */}
      <div className="mb-4 flex gap-1 border-b border-border-subtle">
        {data.groups.map((g) => (
          <button
            key={g.name}
            onClick={() => setActiveTab(g.name)}
            className={clsx(
              "px-3 py-2 text-xs font-medium transition-colors",
              g.name === currentTab
                ? "border-b-2 border-accent text-accent"
                : "text-foreground-secondary hover:text-foreground-primary",
            )}
          >
            {g.name}
          </button>
        ))}
      </div>

      {/* Active group table */}
      {data.groups
        .filter((g) => g.name === currentTab)
        .map((g) => (
          <GroupTable key={g.name} group={g} periods={data.periods} />
        ))}
    </Panel>
  );
}
